import { useEffect, useRef, ReactNode } from 'react';
import { Platform, View } from 'react-native';

// Suppress AbortError from page reload (Supabase client's in-flight token refresh gets cancelled)
// DEV-only: intercept "Unexpected text node" to get component stack trace
if (Platform.OS === 'web' && typeof window !== 'undefined') {
  window.addEventListener('unhandledrejection', (event) => {
    if (event.reason?.name === 'AbortError' || event.reason?.message === 'The operation was aborted.') {
      event.preventDefault();
    }
  });

  if (__DEV__) {
    // Suppress "Unexpected text node: ." warnings from React Native Web
    // These are dev-only (RN Web checks NODE_ENV) and caused by internal
    // whitespace handling in the JSX→RN Web pipeline. Not visible to users.
    const origError = console.error;
    console.error = (...args: any[]) => {
      if (typeof args[0] === 'string' && args[0].includes('Unexpected text node:')) return;
      origError.apply(console, args);
    };
  }
}
import { DarkTheme, DefaultTheme, ThemeProvider } from '@react-navigation/native';
import { Stack } from 'expo-router';
import * as SplashScreen from 'expo-splash-screen';
import { StatusBar } from 'expo-status-bar';
import { useThemeStore } from '@/lib/state/theme-store';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { GestureHandlerRootView } from 'react-native-gesture-handler';
import { KeyboardProvider } from 'react-native-keyboard-controller';
import { SafeAreaProvider } from 'react-native-safe-area-context';
import { AlertProvider } from '@/components/AlertDialog';
import { ErrorBoundary } from '@/components/ErrorBoundary';
import { AuthProvider, useAuth } from '@/lib/supabase';
import { StripeProvider } from '@/lib/stripe';
import { reportError, wasReported } from '@/lib/errors';
import { useReferralFromUrl } from '@/lib/referral';
import FeedbackButton from '@/components/FeedbackButton';
import { DeletionGate } from '@/components/account/DeletionGate';
// ModeBadge is used in individual layouts instead of root
import * as Sentry from '@sentry/react-native';
import { scrubSentryEvent, SENTRY_IGNORE_ERRORS } from '@/lib/sentry';
import { mode } from '@/lib/config/app-mode';
import { startSession, endSession, cleanupSessionListeners } from '@/lib/analytics';
import { startQueryFocusTracking } from '@/lib/query-focus';
import { COMMERCE_ENABLED } from '@/lib/commerce/policy';
// SessionRestorer removed — profile store persistence + landing page Redirect handles session restoration

// Initialize Sentry only in production mode.
// `release`/`dist` are deliberately NOT set here: the SDK auto-detects them from the
// native build (CFBundleShortVersionString@CFBundleVersion / versionName+versionCode),
// and the @sentry/react-native Expo plugin uploads source maps under that SAME
// auto-detected release. Hardcoding `release` (the old `inverio@${EXPO_PUBLIC_APP_VERSION
// || '1.0.1'}` — an env var that was never set, so it pinned to a literal `1.0.1`) made
// the runtime release mismatch the uploaded source maps, so stack traces never
// symbolicated, and it would silently drift on every `autoIncrement` build. `environment`
// is a constant because this block only runs when mode.isProduction.
if (mode.isProduction && process.env.EXPO_PUBLIC_SENTRY_DSN) {
  Sentry.init({
    dsn: process.env.EXPO_PUBLIC_SENTRY_DSN,
    tracesSampleRate: 0.2,
    profilesSampleRate: 0.1,
    enabled: !__DEV__,
    environment: 'production',
    // PII hardening (Sentry is a US-incorporated processor; Inverio handles founder/
    // investor PII). Don't attach default PII, scrub emails/tokens/identifiers from every
    // outgoing event, and drop non-actionable noise. See src/lib/sentry.ts.
    sendDefaultPii: false,
    ignoreErrors: SENTRY_IGNORE_ERRORS,
    beforeSend: (event) => scrubSentryEvent(event),
  });
}

// KeyboardProvider wrapper - only use on native platforms
function KeyboardWrapper({ children }: { children: ReactNode }) {
  if (Platform.OS === 'web') {
    return <>{children}</>;
  }
  return <KeyboardProvider>{children}</KeyboardProvider>;
}

export const unstable_settings = {
  initialRouteName: 'index',
};

// Prevent the splash screen from auto-hiding before asset loading is complete.
// On web, we don't need to prevent auto-hide
if (Platform.OS !== 'web') {
  SplashScreen.preventAutoHideAsync();
}

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 2 * 60 * 1000, // 2 minutes default
      gcTime: 10 * 60 * 1000,   // 10 minutes garbage collection
      retry: (failureCount, error) => {
        // Don't retry auth or validation errors — they won't succeed on retry
        if (error instanceof Error && ['401', '403', '422'].some(c => error.message.includes(c))) {
          return false;
        }
        const msg = (error instanceof Error ? error.message : '').toLowerCase();
        if (msg.includes('unauthorized') || msg.includes('forbidden') || msg.includes('invalid')) return false;
        return failureCount < 2;
      },
    },
    mutations: {
      /**
       * A SAFETY NET for mutations nobody caught — not a second reporter.
       *
       * It used to report unconditionally, so every failure a screen already
       * handled was sent to Sentry TWICE: once from the catch block with a real
       * context (`StartupDetail.handleRequestAccess`) and once from here as
       * `ReactQuery.mutation`. Different contexts mean different fingerprints,
       * so one failure became two issues — INVERIO-APP-W/-V, -S/-T, -P/-Q and
       * -X/-Y were all such pairs, and two occurrences of one bug presented as
       * four separate issues.
       *
       * DEFERRED ON PURPOSE. React Query runs this BEFORE `mutateAsync`
       * rejects, so the screen's catch block — which knows the better context —
       * has not run yet. Waiting a macrotask lets the real report land first;
       * `wasReported` is then true and this stays quiet. If nothing handled it,
       * the mark is absent and the net reports, so unhandled failures are still
       * captured.
       *
       * A thrown primitive cannot carry the mark and will still double-report.
       * Accepted: every mutation failure in this codebase rejects with an Error
       * or a PostgrestError object.
       */
      onError: (error) => {
        setTimeout(() => {
          if (wasReported(error)) return;
          reportError(error, { context: 'ReactQuery.mutation', level: 'warning' });
        }, 0);
      },
    },
  },
});

function RootLayoutNav({ colorScheme }: { colorScheme: 'light' | 'dark' | null | undefined }) {
  // Auto-detect referral codes from URL params
  useReferralFromUrl();

  // Hide splash screen once layout is ready (native only)
  useEffect(() => {
    if (Platform.OS !== 'web') {
      SplashScreen.hideAsync();
    }
  }, []);

  return (
    <ThemeProvider value={colorScheme === 'dark' ? DarkTheme : DefaultTheme}>
      <Stack>
        <Stack.Screen name="index" options={{ headerShown: false }} />
        <Stack.Screen name="(auth)" options={{ headerShown: false }} />
        <Stack.Screen name="(onboarding)" options={{ headerShown: false }} />
        <Stack.Screen name="(startup)" options={{ headerShown: false }} />
        <Stack.Screen name="(investor)" options={{ headerShown: false }} />
        <Stack.Screen name="(admin)" options={{ headerShown: false }} />
        <Stack.Screen name="startup-detail" options={{ headerShown: false }} />
        {/* A shared company artifact, reachable from both profiles. Registered
            on the root Stack rather than in either persona's Tabs: it is a
            detail route, not a sixth destination. */}
        <Stack.Screen name="strategic-analysis" options={{ headerShown: false }} />
        <Stack.Screen name="login" options={{ headerShown: false }} />
        <Stack.Screen name="startup-team" options={{ headerShown: false }} />
        <Stack.Screen name="edit-profile" options={{ headerShown: false }} />
        <Stack.Screen name="edit-company" options={{ headerShown: false }} />
        <Stack.Screen name="settings" options={{ headerShown: false }} />
        <Stack.Screen name="security" options={{ headerShown: false }} />
        <Stack.Screen name="change-password" options={{ headerShown: false }} />
        <Stack.Screen name="forgot-password" options={{ headerShown: false }} />
        <Stack.Screen name="reset-password" options={{ headerShown: false }} />
        <Stack.Screen name="mfa-verify" options={{ headerShown: false }} />
        <Stack.Screen name="delete-account" options={{ headerShown: false }} />
        <Stack.Screen name="manage-data" options={{ headerShown: false }} />
        {/* No swipe-back: the pending-deletion screen has exactly two exits. */}
        <Stack.Screen name="account-deletion-pending" options={{ headerShown: false, gestureEnabled: false }} />
        <Stack.Screen name="help" options={{ headerShown: false }} />
        <Stack.Screen name="notifications" options={{ headerShown: false }} />
        <Stack.Screen name="notification-settings" options={{ headerShown: false }} />
        <Stack.Screen name="discover-startups" options={{ headerShown: false }} />
        <Stack.Screen name="discover-investors" options={{ headerShown: false }} />
        <Stack.Screen name="deal-comparison" options={{ headerShown: false }} />
        <Stack.Screen name="company-directory" options={{ headerShown: false }} />

        <Stack.Screen name="select-role" options={{ headerShown: false }} />
        <Stack.Screen name="terms" options={{ headerShown: false }} />
        <Stack.Screen name="privacy" options={{ headerShown: false }} />
        <Stack.Screen name="cookies" options={{ headerShown: false }} />
        <Stack.Screen name="refunds" options={{ headerShown: false }} />
        <Stack.Screen name="disclaimer" options={{ headerShown: false }} />
        <Stack.Screen name="about" options={{ headerShown: false }} />
        <Stack.Screen name="contact" options={{ headerShown: false }} />
        <Stack.Screen name="submit/[slug]" options={{ headerShown: false }} />
        <Stack.Screen name="claim/[token]" options={{ headerShown: false }} />
        <Stack.Screen name="invite/team" options={{ headerShown: false }} />

        {/* Commerce lives on the web only (App Store 3.1.3(f), src/lib/commerce/policy.ts).
            On native these routes do not exist: a navigation or deep link to one
            lands on the anchor route instead. check-native-commerce.ts asserts
            each is declared here and nowhere else. */}
        <Stack.Protected guard={COMMERCE_ENABLED}>
          <Stack.Screen name="pricing" options={{ headerShown: false }} />
          <Stack.Screen name="referral" options={{ headerShown: false }} />
          <Stack.Screen name="subscription-success" options={{ headerShown: false }} />
          <Stack.Screen name="subscription-cancel" options={{ headerShown: false }} />
        </Stack.Protected>
      </Stack>
      {/* Pending-deletion gate (118-07): a sibling overlay, not a wrapper —
          the navigator is not wrapped. It sees every route, deep links included. */}
      <DeletionGate />
    </ThemeProvider>
  );
}



// Lazy-load push notifications to avoid expo-notifications import on web
const PushNotificationInitializer = Platform.OS === 'web'
  ? () => null
  : require('@/lib/notifications/PushInitializer').default;

/**
 * Forward foreground/background transitions to React Query.
 *
 * OUTSIDE `AuthProvider` in spirit though not in tree position: focus is a
 * property of the app, not of a signed-in user, and a query that refetches on
 * return to the app should do so whether or not anyone is authenticated.
 * Mounted once; see `startQueryFocusTracking` for what was broken without it.
 */
function QueryFocusTracker() {
  useEffect(() => startQueryFocusTracking(), []);
  return null;
}

function AnalyticsInitializer() {
  const { user } = useAuth();
  const userIdRef = useRef<string | null>(null);

  useEffect(() => {
    if (user && mode.useRealAPI && user.id !== userIdRef.current) {
      userIdRef.current = user.id;
      startSession(user.id);
    }
    if (!user && userIdRef.current) {
      endSession(userIdRef.current);
      userIdRef.current = null;
    }
    return () => {
      if (userIdRef.current) endSession(userIdRef.current);
      cleanupSessionListeners();
    };
  }, [user]);

  return null;
}

function RootLayout() {
  const isDark = useThemeStore((s) => s.isDark);

  // Set document title for web browser tab
  useEffect(() => {
    if (Platform.OS === 'web') {
      document.title = 'Inverio';
    }
  }, []);

  return (
    <ErrorBoundary>
      <QueryClientProvider client={queryClient}>
        <StripeProvider publishableKey={process.env.EXPO_PUBLIC_STRIPE_PUBLISHABLE_KEY}>
          <AuthProvider>
            <QueryFocusTracker />
            <AnalyticsInitializer />
            <PushNotificationInitializer />
            <SafeAreaProvider>
              <GestureHandlerRootView style={{ flex: 1 }}>
                <KeyboardWrapper>
                  <AlertProvider>
                    <StatusBar style={isDark ? 'light' : 'dark'} />
                    <RootLayoutNav colorScheme={isDark ? 'dark' : 'light'} />
                    <FeedbackButton />
                  </AlertProvider>
                </KeyboardWrapper>
              </GestureHandlerRootView>
            </SafeAreaProvider>
          </AuthProvider>
        </StripeProvider>
      </QueryClientProvider>
    </ErrorBoundary>
  );
}

// Wrap the root component with Sentry for error boundary and performance monitoring
export default Sentry.wrap(RootLayout);